import React from 'react';
import { Shield, UserPlus, AlertCircle, Mail, CheckCircle, XCircle, DollarSign, Users, Receipt, PiggyBank, Settings, UserCheck, Edit2, MessageSquare, Trash2, Database, Clock, LogIn } from 'lucide-react';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, Timestamp, onSnapshot, writeBatch } from 'firebase/firestore';
import { db, createUser, deleteUserAccount, startImpersonation } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import DeleteUserDialog from './DeleteUserDialog';
import FirebaseTest from './FirebaseTest';

interface User {
  id: string;
  email: string;
  status: 'pending' | 'active' | 'rejected' | 'deactivated';
  createdAt: Date;
  lastLogin?: Date;
  isAdmin?: boolean;
}

function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = React.useState<User[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<{ id: string; email: string } | null>(null);
  const [broadcastMessage, setBroadcastMessage] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!user?.isAdmin) return;

    const usersQuery = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        lastLogin: doc.data().lastLogin?.toDate()
      })) as User[];
      setUsers(usersList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    });

    return () => unsubscribe();
  }, [user?.isAdmin]);

  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const handleToggleAdmin = async (userId: string, isAdmin: boolean) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isAdmin: !isAdmin,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error toggling admin status:', error);
    }
  };

  const handleBroadcastSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;

    setLoading(true);
    try {
      // Deactivate all existing broadcast messages
      const existingBroadcasts = await getDocs(
        query(collection(db, 'broadcastMessages'), where('active', '==', true))
      );

      const batch = writeBatch(db);
      existingBroadcasts.docs.forEach(doc => {
        batch.update(doc.ref, { active: false });
      });
      await batch.commit();

      // Create new broadcast message
      await addDoc(collection(db, 'broadcastMessages'), {
        message: broadcastMessage,
        createdAt: Timestamp.now(),
        createdBy: user?.email,
        active: true
      });

      setBroadcastMessage('');
    } catch (error) {
      console.error('Error sending broadcast:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonateUser = (targetUser: User) => {
    if (!user) return;

    // Store impersonation data and reload
    startImpersonation(user.uid, user.email || '', targetUser.id, targetUser.email);
    window.location.reload();
  };

  if (!user?.isAdmin) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* System Overview */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Users className="w-6 h-6 mr-2" />
            System Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                </div>
                <Users className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Approvals</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {users.filter(u => u.status === 'pending').length}
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Active Users</p>
                  <p className="text-2xl font-bold text-green-600">
                    {users.filter(u => u.status === 'active').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Firebase Status */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <Database className="w-6 h-6 mr-2" />
              Database Status
            </h2>
          </div>
          <FirebaseTest />
        </div>

        {/* Broadcast Message */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <MessageSquare className="w-6 h-6 mr-2" />
            Broadcast Message
          </h2>
          <div className="bg-white rounded-lg shadow p-6">
            <form onSubmit={handleBroadcastSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter a message to broadcast to all users..."
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Broadcast
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* User Management */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Settings className="w-6 h-6 mr-2" />
            User Management
          </h2>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {users.map((u) => (
                <li key={u.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{u.email}</p>
                        {u.isAdmin && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Admin
                          </span>
                        )}
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.status === 'active' ? 'bg-green-100 text-green-800' :
                          u.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          u.status === 'deactivated' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 space-y-1">
                        <p>Joined {u.createdAt.toLocaleDateString()}</p>
                        {u.lastLogin && (
                          <p className="flex items-center">
                            <Clock className="w-3.5 h-3.5 mr-1 text-gray-400" />
                            <span className="font-medium text-gray-600">Last login:</span>
                            <span className="ml-1">
                              {u.lastLogin.toLocaleDateString()} at {u.lastLogin.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </p>
                        )}
                        {!u.lastLogin && u.status === 'active' && (
                          <p className="flex items-center text-gray-400 italic">
                            <Clock className="w-3.5 h-3.5 mr-1" />
                            Never logged in
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {u.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(u.id, 'active')}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(u.id, 'rejected')}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                      {u.status === 'active' && (
                        <button
                          onClick={() => handleStatusChange(u.id, 'deactivated')}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Deactivate
                        </button>
                      )}
                      {u.status === 'deactivated' && (
                        <button
                          onClick={() => handleStatusChange(u.id, 'active')}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Activate
                        </button>
                      )}
                      {u.status === 'active' && u.id !== user?.uid && (
                        <button
                          onClick={() => handleImpersonateUser(u)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          title="Login as this user for support"
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Login as User
                        </button>
                      )}
                      {!u.isAdmin && u.status === 'active' && (
                        <button
                          onClick={() => handleToggleAdmin(u.id, false)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Make Admin
                        </button>
                      )}
                      {u.isAdmin && u.email !== 'ogoudge@outlook.com' && (
                        <button
                          onClick={() => handleToggleAdmin(u.id, true)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Remove Admin
                        </button>
                      )}
                      {!u.isAdmin && (
                        <button
                          onClick={() => {
                            setUserToDelete({ id: u.id, email: u.email });
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}

              {users.length === 0 && (
                <li className="px-4 py-12 text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding users.</p>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Delete User Dialog */}
      {showDeleteDialog && userToDelete && (
        <DeleteUserDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setUserToDelete(null);
          }}
          onConfirm={async () => {
            if (userToDelete) {
              await deleteUserAccount(userToDelete.id);
            }
          }}
          userEmail={userToDelete.email}
        />
      )}
    </div>
  );
}

export default Admin;